import { w3cwebsocket as W3CWebSocket } from 'websocket';
import axios from 'axios';
import { ChatAgentType } from '@constants/chat-agents';
import type { ChatMessage } from '../types';
import {
    type ChatMessageResponse,
    type ChatHistoryResponse,
    type ForwardedImage,
} from '../types/chat-message';
import { processAndEmitMessage, convertToChatMessage } from '@utils/chat-message';
import chatHttpClient from '@http/chat-http-client';
import { generateUUID } from '@utils/uuid';


import { REACT_APP_WS_URL, REACT_APP_USER_API_DOMAIN, REACT_APP_SERVICE_PREFIX, REACT_APP_ENV, REACT_APP_API_DOMAIN } from '../config';
import type {
    ComponentType,
    ChatComponentItem,
    ChatComponentListPage,
} from '../types/chat-component';
import { buildComponentAttachments } from '../types/chat-component';

export type {
    ComponentType,
    ChatComponentItem,
    ChatComponentAttachment,
    ChatComponentListPage,
} from '../types/chat-component';

const WS_URL = REACT_APP_WS_URL;
const USER_API_DOMAIN = REACT_APP_USER_API_DOMAIN;
const CHAT_REST_BASE_URL = REACT_APP_API_DOMAIN;

/**
 * Get service prefix based on environment
 * Matches backend logic:
 *   PRODUCTION / STAGING → "chat-service"
 *   GEM → "" (no prefix)
 *   default (dev) → "chat-service-dev"
 *
 * Can be overridden via REACT_APP_SERVICE_PREFIX env variable
 */
const getServicePrefix = (): string => {
    // // Allow explicit override via env variable
    // if (REACT_APP_SERVICE_PREFIX !== undefined) {
    //     return REACT_APP_SERVICE_PREFIX;
    // }
    const env = (REACT_APP_ENV || 'development').toLowerCase();
    switch (env) {
        case 'production':
            return 'chat-service'
        case 'prod':
            return 'chat-service'
        case 'staging':

            return 'chat-service-dev';
        case 'gem':
            return '';
        default:
            return 'chat-service-dev';
    }
};

const SERVICE_PREFIX = getServicePrefix();
/** Build API path with dynamic service prefix */
const apiPath = (path: string): string => {
    if (!SERVICE_PREFIX) return path; // GEM env: no prefix
    return `/${SERVICE_PREFIX}${path}`;
};

/** Message-service routes go through ai-gateway (same pattern as quick-suggestions). */
const messageServicePath = (path: string): string =>
    `/ai-gateway/api/proxy/message-service${path}`;

export const MESSAGE_SESSION_STORAGE_KEY = 'current_message_session_id';

export interface MessageSession {
    id: string;
    name: string | null;
    typeChat: string;
    status: string;
    messageCount: number;
    lastMessageAt: string | null;
    createdAt: string | null;
}

export interface MessageSessionsPage {
    data: MessageSession[];
    nextCursor: string | null;
    previousCursor: string | null;
    hasMore: boolean;
    hasPrev: boolean;
}

export type MessageSessionsDirection = 'NEXT' | 'PREV';

export interface GetMessageSessionsParams {
    size?: number;
    typeChat?: string;
    keyword?: string;
    nextCursor?: string | null;
    previousCursor?: string | null;
    direction?: MessageSessionsDirection;
}

export interface CurrentMessageSession {
    sessionId: string;
    newSession: boolean;
}

const parseMessageSessionId = (response: any): string | null => {
    const raw = response?.data ?? response;
    const session = Array.isArray(raw) ? raw[0] : raw;
    if (!session) return null;

    const id =
        session.id ??
        session.sessionId ??
        session.session_id ??
        session?.session?.id ??
        session?.session?.sessionId;

    return id != null && String(id).trim() ? String(id) : null;
};

const persistMessageSessionId = (sessionId: string | null) => {
    if (sessionId) {
        globalThis.sessionStorage.setItem(MESSAGE_SESSION_STORAGE_KEY, sessionId);
    } else {
        globalThis.sessionStorage.removeItem(MESSAGE_SESSION_STORAGE_KEY);
    }
    return sessionId;
};

export const setMessageSessionId = (sessionId: string | null) =>
    persistMessageSessionId(sessionId);

export const getMessageSessionId = (): string | null => {
    const id = globalThis.sessionStorage.getItem(MESSAGE_SESSION_STORAGE_KEY);
    return id != null && String(id).trim() ? String(id) : null;
};

const mapMessageSession = (raw: any): MessageSession | null => {
    const id = raw?.id ?? raw?.sessionId ?? raw?.session_id;
    if (id == null || !String(id).trim()) return null;

    return {
        id: String(id),
        name: raw?.name ?? null,
        typeChat: String(raw?.typeChat ?? raw?.type_chat ?? ''),
        status: String(raw?.status ?? ''),
        messageCount: Number(raw?.messageCount ?? raw?.message_count ?? 0),
        lastMessageAt: raw?.lastMessageAt ?? raw?.last_message_at ?? null,
        createdAt: raw?.createdAt ?? raw?.created_at ?? null,
    };
};

const parseMessageSessionsPage = (response: any): MessageSessionsPage => {
    const root = response?.data ?? response;
    const page = root?.data && !Array.isArray(root.data) && Array.isArray(root.data.data)
        ? root.data
        : root;
    const list = Array.isArray(page?.data)
        ? page.data
        : Array.isArray(page)
            ? page
            : [];

    return {
        data: list
            .map(mapMessageSession)
            .filter((item: MessageSession | null): item is MessageSession => !!item),
        nextCursor: page?.nextCursor ?? page?.next_cursor ?? null,
        previousCursor: page?.previousCursor ?? page?.previous_cursor ?? null,
        hasMore: Boolean(page?.hasMore ?? page?.has_more ?? false),
        hasPrev: Boolean(page?.hasPrev ?? page?.has_prev ?? false),
    };
};

/**
 * Build WebSocket path based on environment.
 * Dev uses /dev, while staging/production use the base path.
 */
const getWebSocketPathPrefix = (): string => {
    const env = (REACT_APP_ENV || 'development').toLowerCase();
    return env === 'development' ? '/dev' : '';
};

const WS_PATH_PREFIX = getWebSocketPathPrefix();

export interface WebSocketMessage {
    message: ChatMessageResponse;
}

export interface AuthAccount {
    id?: string;
    full_name?: string;
    email?: string;
    phone_number?: string;
    role_bitmask?: number;
    is_active?: boolean;
}

export interface AuthInfo {
    account: AuthAccount;
    currentBusinessId: string | null;
}

export interface UserPermission {
    isOwner: boolean;
    permissions: Record<string, boolean>;
}

export type AiDisplayPosition = 'all' | 'home';

export interface AiDisplayJsonItem {
    key: 'display' | 'main_screen' | 'all_screen' | string;
    active: boolean;
}

export interface AiDisplaySetting {
    id: string;
    referenceId: string | null;
    jsonValue: AiDisplayJsonItem[];
    isVisible: boolean;
    position: AiDisplayPosition;
}

const getBusinessIdFromToken = (token: string): string | null => {
    try {
        const payload = JSON.parse(atob(token.split('.')[1] || ''));
        return payload?.business_id || null;
    } catch {
        return null;
    }
};

const formatOrderedAt = (value?: string | null): string | undefined => {
    if (!value) return undefined;
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return value;
    const pad = (n: number) => String(n).padStart(2, '0');
    // Display in local time (GMT+7 expected for VN users)
    return `${pad(date.getHours())}:${pad(date.getMinutes())} ${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;
};

const extractListRows = (payload: any): any[] => {
    const data = payload?.data ?? payload;
    if (Array.isArray(data)) return data;
    if (Array.isArray(data?.data)) return data.data;
    if (Array.isArray(data?.items)) return data.items;
    if (Array.isArray(data?.content)) return data.content;
    if (Array.isArray(data?.records)) return data.records;
    return [];
};

const extractHasMore = (payload: any, page: number, pageSize: number, itemCount: number): boolean => {
    const data = payload?.data ?? payload;
    if (typeof data?.has_more === 'boolean') return data.has_more;
    if (typeof data?.hasMore === 'boolean') return data.hasMore;
    if (typeof data?.meta?.has_more === 'boolean') return data.meta.has_more;
    if (typeof data?.total === 'number') return page * pageSize < data.total;
    if (typeof data?.total_count === 'number') return page * pageSize < data.total_count;
    if (typeof data?.totalElements === 'number') return page * pageSize < data.totalElements;
    return itemCount >= pageSize;
};

const mapOrderItem = (row: any): ChatComponentItem => {
    const orderNumber = String(row.order_number ?? row.orderNumber ?? row.code ?? row.id ?? '');
    return {
        type: 'order',
        id: String(row.id ?? orderNumber),
        displayName: orderNumber,
        subtitle: formatOrderedAt(row.ordered_at ?? row.orderedAt ?? row.updated_at),
        raw: row,
    };
};

const mapProductItem = (row: any): ChatComponentItem => {
    const images = row.image_urls ?? row.imageUrls ?? [];
    const firstImage = Array.isArray(images) ? images[0] : undefined;
    return {
        type: 'product',
        id: String(row.id ?? row.product_id ?? row.product_code ?? ''),
        displayName: String(row.name ?? ''),
        avatarUrl: typeof firstImage === 'string' ? firstImage : firstImage?.url,
        raw: row,
    };
};

const mapIngredientItem = (row: any): ChatComponentItem => ({
    type: 'ingredient',
    id: String(row.id ?? row.material_id ?? row.material_code ?? ''),
    displayName: String(row.name ?? ''),
    raw: row,
});

const mapCustomerItem = (row: any): ChatComponentItem => ({
    type: 'customer',
    id: String(row.id ?? row.contact_id ?? ''),
    displayName: String(row.contact_name ?? row.name ?? ''),
    subtitle: row.contact_phone ?? row.phone_number ?? row.phone ?? undefined,
    avatarUrl: row.avatar_url ?? row.avatarUrl ?? undefined,
    raw: row,
});

const mapSupplierItem = (row: any): ChatComponentItem => ({
    type: 'supplier',
    id: String(row.id ?? row.contact_id ?? ''),
    displayName: String(row.contact_name ?? row.name ?? ''),
    subtitle: row.contact_phone ?? row.phone_number ?? row.phone ?? undefined,
    avatarUrl: row.avatar_url ?? row.avatarUrl ?? undefined,
    raw: row,
});

const mapEmployeeItem = (row: any): ChatComponentItem => ({
    type: 'employee',
    id: String(row.id ?? row.staff_id ?? ''),
    displayName: String(row.staff_name ?? row.name ?? ''),
    subtitle: row.staff_phone ?? row.phone_number ?? row.phone ?? undefined,
    avatarUrl: row.staff_avatar_url ?? row.avatar_url ?? row.avatarUrl ?? undefined,
    raw: row,
});

const authHeaders = (token: string) => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${token}`,
});

const parseAiDisplaySetting = (response: any): AiDisplaySetting | null => {
    const raw = response?.data ?? response;
    const setting = Array.isArray(raw) ? raw[0] : raw;
    if (!setting) return null;

    const id = setting.id || setting.setting_id || setting.settingId;
    if (!id) return null;

    const jsonValue: AiDisplayJsonItem[] =
        setting.json_value ?? setting.jsonValue ?? [];

    const getActive = (key: string, fallback = false) => {
        const item = jsonValue.find((entry) => entry.key === key);
        return typeof item?.active === 'boolean' ? item.active : fallback;
    };

    const isVisible = getActive('display', true);
    const allScreen = getActive('all_screen', false);
    const mainScreen = getActive('main_screen', true);

    return {
        id: String(id),
        referenceId: setting.reference_id ?? setting.referenceId ?? null,
        jsonValue,
        isVisible,
        position: allScreen ? 'all' : mainScreen ? 'home' : 'home',
    };
};

const buildAiDisplayJsonValue = (
    isVisible: boolean,
    position: AiDisplayPosition,
): AiDisplayJsonItem[] => [
    { key: 'display', active: isVisible },
    { key: 'main_screen', active: isVisible && position === 'home' },
    { key: 'all_screen', active: isVisible && position === 'all' },
];


const normalizeAuthInfo = (payload: any): AuthInfo => {
    const data = payload?.data ?? {};
    const raw = data.account ?? data.user_info ?? data.userInfo ?? {};
    const business = data.business_info ?? data.businessInfo ?? {};

    return {
        account: {
            id: raw.id,
            full_name: raw.display_name ?? raw.full_name,
            email: raw.primary_email ?? raw.email,
            phone_number: raw.mobile_number ?? raw.phone_number,
            role_bitmask: raw.role_bitmask ?? raw.user_roles,
            is_active: raw.is_active,
        },
        currentBusinessId:
            business.current_store?.id ?? business.current_business?.id ?? null,
    };
};


export const parseHistoryMessages = (
    historyItems: any[],
    agentType?: ChatAgentType
): ChatMessage[] => {
    const messages: ChatMessage[] = [];
    // Sort by createdAt ascending (oldest first) to ensure correct chronological order
    const sortedItems = [...historyItems].sort((a, b) => {
        const timeA = new Date(a.createdAt || a.time || 0).getTime();
        const timeB = new Date(b.createdAt || b.time || 0).getTime();
        return timeA - timeB;
    });

    sortedItems.forEach((msg: any) => {
        const chatMessage = convertToChatMessage(msg as ChatMessageResponse, agentType, true);
        messages.push(chatMessage);
    });

    return messages;
};

export const connectChatWebSocket = (
    conversationId: number,
    token: string,
    onMessage: (chatMessage: ChatMessage) => void,
    onHistoryLoaded: (history: ChatHistoryResponse) => void,
    onError: (error: Error) => void,
    onOpen?: () => void,
    loadHistory: boolean = true,
    typeChat: ChatAgentType = ChatAgentType.AI
) => {
    const wsUrl = `${WS_URL}${WS_PATH_PREFIX}/api/ws/chat?conversationId=${conversationId}&token=${token}`;
    const socket = new W3CWebSocket(wsUrl);

    socket.onopen = () => {
        (async () => {
            console.log('WebSocket Connected');
            onOpen?.();
            try {
                if (loadHistory) {
                    const history = await getChatHistory(token, '', typeChat);

                    // === FAKE EMPTY HISTORY FOR TESTING ===
                    // if (history?.data) history.data.data = [];

                    if (history?.data?.data && Array.isArray(history.data.data)) {
                        // Sort by createdAt ascending (oldest first) to ensure correct chronological order
                        const sortedHistory = [...history.data.data].sort((a, b) => {
                            const timeA = new Date(a.createdAt || 0).getTime();
                            const timeB = new Date(b.createdAt || 0).getTime();
                            return timeA - timeB;
                        });
                        sortedHistory.forEach((msg: ChatMessageResponse) => {
                            processAndEmitMessage(msg, onMessage, typeChat, true);
                        });
                        onHistoryLoaded(history);
                    } else {
                        console.warn('[WS History] Unexpected data format:', {
                            hasData: !!history?.data,
                            dataType: typeof history?.data?.data,
                            historyKeys: history ? Object.keys(history) : [],
                            dataKeys: history?.data ? Object.keys(history.data) : [],
                        });
                        onHistoryLoaded(history);
                    }
                }
                const history = await getWelcomeMessage(token, typeChat);
                console.log(132, history)

            } catch (error) {
                console.error('Error loading chat history:', error);
                onError(error as Error);
            }
        })();
    };

    socket.onmessage = (event) => {
        try {
            // IMPORTANT: Large Snowflake IDs (e.g. 324797755372507136) exceed Number.MAX_SAFE_INTEGER
            // and lose precision when parsed by standard JSON.parse().
            // We replace bare large integers in the JSON string with quoted strings BEFORE parsing.
            const rawText: string = event.data.toString();
            const safeText = rawText.replace(
                /:\s*(\d{16,})/g,
                (_, digits) => `: "${digits}"`
            );
            const data: WebSocketMessage = JSON.parse(safeText);
            console.log('WebSocket message received:', data);

            if (data.message) {
                processAndEmitMessage(data.message, onMessage, typeChat);
            }
        } catch (error) {
            console.error('Error processing WebSocket message:', error);
        }
    };


    socket.onclose = () => {
        console.log('WebSocket Disconnected');
    };

    socket.onerror = (error) => {
        console.error('WebSocket Error:', error);
        if (onError) {
            onError(error);
        }
    };

    return socket;
};

export const sendChatMessage = (
    socket: W3CWebSocket | null,
    message: string,
    conversationId: number,
    onError?: (error: Error) => void,
    typeChat: ChatAgentType = ChatAgentType.AI,
    buttonId?: number,
    isHidden: boolean = false,
    replyToMessageId?: string | number,
    replyToCodeMessage?: string,
    codeMessage?: string,
    images?: Array<{ contentType: string; imageUrl: string; name: string; uploadStatus: string }>,
    location: string = '',
    /** Thành phần đã chọn — chỉ khi có mới thêm `attachments` lên socket. */
    components?: ChatComponentItem[],
) => {
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        const error = new Error('WebSocket is not connected');
        onError?.(error);
        return false;
    }

    try {
        const messageData: any = {
            content: message,
            conversationId,
            device: "web",
            isHidden,
            location,
            typeChat: typeChat,
            version: "4",
            button: buttonId,
            stream: true,
            codeMessage: codeMessage || generateUUID(),
        };

        const sessionId = globalThis.sessionStorage.getItem(MESSAGE_SESSION_STORAGE_KEY);
        if (sessionId) {
            messageData.sessionId = sessionId;
        }

        // Quote reply fields
        if (replyToMessageId) {
            messageData.replyToMessageId = replyToMessageId;
        }
        if (replyToCodeMessage) {
            messageData.replyToCodeMessage = replyToCodeMessage;
        }

        // Image fields
        if (images && images.length > 0) {
            messageData.images = images;
        }

        // Chỉ khi có thành phần: thêm attachments (mock), không đụng field khác
        if (components && components.length > 0) {
            messageData.attachments = components.flatMap(buildComponentAttachments);
        }

        socket.send(JSON.stringify(messageData));
        return true;
    } catch (error) {
        console.error('Error sending message:', error);
        onError?.(error as Error);
        return false;
    }
};

/**
 * Send forward message to change agent flow
 */
export const sendForwardMessage = (
    socket: W3CWebSocket | null,
    conversationId: number,
    typeChangeFlow: string,
    targetAgent: ChatAgentType,
    msgUserFwToNewAgent: string | undefined,
    msgBotFwToNewAgentId: string | undefined,
    msgUserFwCodeMessage: string | undefined,
    onError?: (error: Error) => void,
    typeChat: ChatAgentType = ChatAgentType.AI,
    msgUserImagesFw?: ForwardedImage[],
    sessionId?: string | null,
) => {

    console.log(243, msgUserImagesFw)
    if (!socket || socket.readyState !== WebSocket.OPEN) {
        const error = new Error('WebSocket is not connected');
        onError?.(error);
        return false;
    }

    try {
        const CONTENT_FORWARD_SOCKET = "Chuyển đổi trợ lý";
        const messageData: Record<string, unknown> = {
            content: CONTENT_FORWARD_SOCKET,
            conversationId,
            device: "web",
            isHidden: true,
            location: "",
            typeChat: typeChat,
            version: "4",
            typeChangeFlow: typeChangeFlow,
            targetAgent: targetAgent,
            msgUserFwToNewAgent: msgUserFwToNewAgent,
            msgBotFwToNewAgentId: msgBotFwToNewAgentId,
            msgUserFwCodeMessage: msgUserFwCodeMessage,
            msgUserImagesFw: msgUserImagesFw,
        };
        if (sessionId) {
            messageData.sessionId = sessionId;
        }
        console.log('[Forward Socket] Sending hidden message:', messageData);
        socket.send(JSON.stringify(messageData));
        console.log('[Forward Socket] Message sent successfully');
        return true;
    } catch (error) {
        console.error('Error sending forward message:', error);
        onError?.(error as Error);
        return false;
    }
};

/**
 * Get chat history
 */
/**
 * Normalize history API payloads (chat-service / message-service / gateway wrappers).
 */
const normalizeChatHistoryResponse = (payload: any): ChatHistoryResponse => {
    if (!payload) {
        return { data: { data: [] } };
    }

    // { code, message, data: { data: [], nextCursor, hasMore } }
    if (Array.isArray(payload?.data?.data)) {
        return {
            data: {
                data: payload.data.data,
                nextCursor: payload.data.nextCursor ?? undefined,
                hasMore: payload.data.hasMore,
            },
        };
    }

    // { data: { data: [], nextCursor } } already ChatHistoryResponse-like
    if (payload?.data && Array.isArray(payload.data.data)) {
        return payload as ChatHistoryResponse;
    }

    // { data: Message[], nextCursor, hasMore }
    if (Array.isArray(payload?.data)) {
        return {
            data: {
                data: payload.data,
                nextCursor: payload.nextCursor ?? undefined,
                hasMore: payload.hasMore,
            },
        };
    }

    // { content: Message[] } (some list endpoints)
    if (Array.isArray(payload?.content)) {
        return {
            data: {
                data: payload.content,
                nextCursor: payload.nextCursor ?? undefined,
                hasMore: payload.hasMore,
            },
        };
    }

    return { data: { data: [] } };
};

/**
 * Get chat history
 */
export const getChatHistory = async (
    token: string,
    nextCursor: string = '',
    typeChat: ChatAgentType = ChatAgentType.AI,
    sessionId?: string | null,
    options?: { signal?: AbortSignal },
): Promise<ChatHistoryResponse> => {
    try {
        if (typeChat === ChatAgentType.CSKH) {
            // CSKH uses GET /history-omi endpoint
            const params: any = {
                typeChat: 'CSKH',
                direction: 'NEXT',
                v: '3',
                size: '10',
            };
            if (nextCursor) {
                params.nextCursor = nextCursor;
            }
            const response = await chatHttpClient.get(
                apiPath(`/api/chat/messages/history-omi`),
                {
                    params,
                    headers: {
                        'Authorization': `Bearer ${token}`
                    },
                    signal: options?.signal,
                }
            );
            return normalizeChatHistoryResponse(response.data);
        }

        // AI + other agents: POST /chat-service.../api/chat/messages/search
        // Pass sessionId to load messages for a specific conversation session.
        const resolvedSessionId =
            typeChat === ChatAgentType.AI
                ? ((sessionId != null && String(sessionId).trim()
                    ? String(sessionId)
                    : null) ||
                    globalThis.sessionStorage.getItem(MESSAGE_SESSION_STORAGE_KEY) ||
                    null)
                : null;

        const response = await chatHttpClient.post(
            apiPath(`/api/chat/messages/search?typeChat=${typeChat}&v=3`),
            {
                pageable: { size: '10' },
                nextCursor: nextCursor || '',
                typeChat,
                device: 'web',
                ...(resolvedSessionId ? { sessionId: resolvedSessionId } : {}),
            },
            {
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
                signal: options?.signal,
            },
        );
        return normalizeChatHistoryResponse(response.data);
    } catch (error) {
        if (axios.isCancel(error) || (error as any)?.code === 'ERR_CANCELED') {
            throw error;
        }
        console.error('Error fetching chat history:', error);
        throw error;
    }
};

/**
 * Get page index for a specific message (CSKH only)
 * Returns a page of messages that contains the target message
 */
export const getMessagePageIndex = async (
    token: string,
    messageId: string | number,
    size: number = 10
): Promise<any> => {
    try {
        const response = await chatHttpClient.get(
            apiPath(`/api/chat/messages/page-index`),
            {
                params: { messageId, size, v: '3' },
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching message page index:', error);
        throw error;
    }
};

/**
 * Get newer CSKH history (direction=PREV with previousCursor)
 * Used when scrolling down after jumping to a specific message page
 */
export const getChatHistoryCSKHNewer = async (
    token: string,
    previousCursor: string
): Promise<ChatHistoryResponse> => {
    try {
        const params: any = {
            typeChat: 'CSKH',
            direction: 'PREV',
            v: '3',
            size: '10',
            previousCursor,
        };
        const response = await chatHttpClient.get<ChatHistoryResponse>(
            apiPath(`/api/chat/messages/history-omi`),
            {
                params,
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching CSKH newer history:', error);
        throw error;
    }
};

/**
 * Get default commands
 */
export const getDefaultCommands = async (token: string, typeChat: ChatAgentType = ChatAgentType.AI) => {
    try {
        const response = await chatHttpClient.get(
            apiPath(`/api/chat/command/default?v=2&typeChat=${typeChat}`),
            {
                headers: {
                    'Authorization': `Bearer ${token}`
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching default commands:', error);
        throw error;
    }
};

export const getWelcomeMessage = async (token: string, typeChat: ChatAgentType = ChatAgentType.AI) => {
    try {
        const response = await chatHttpClient.get(
            apiPath(`/api/chat/messages/welcome-message?typeChat=${typeChat}&v=3`),
            {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`,
                }
            }
        );
        return response.data;
    } catch (error) {
        console.error('Error fetching welcome message:', error);
        throw error;
    }
};

export const chatService = {

    getConversations: async (token: string) => {
        try {
            const response = await chatHttpClient.post(
                apiPath(`/api/chat/conversations`),
                {},
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Lỗi khi lấy danh sách hội thoại:', error);
            throw error;
        }
    },
    getChatHistory,
    getMessagePageIndex,
    getChatHistoryCSKHNewer,
    parseHistoryMessages,
    getDefaultCommands,
    getWelcomeMessage,
    getInstructionSuggestions: async (token: string) => {
        try {
            const response = await chatHttpClient.get(
                '/ai-gateway/api/proxy/router-service/api/v2/instruction-manual/search?platform=web&page=0&size=100',
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching instruction suggestions:', error);
            throw error;
        }
    },

    getQuickFunctionsDefault: async (token: string) => {
        try {
            const response = await chatHttpClient.get(
                apiPath(`/api/chat/command/quick-function/default`),
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching quick functions:', error);
            throw error;
        }
    },

    getQuickFunctions: async (token: string) => {
        try {
            const response = await chatHttpClient.get(
                apiPath(`/api/chat/command/quick-function`),
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching quick functions:', error);
            throw error;
        }
    },

    getQuickSuggestions: async (token: string) => {
        try {
            const response = await chatHttpClient.get(
                '/ai-gateway/api/proxy/chat-service/api/chat/quick-suggestions',
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                },
            );
            return response.data;
        } catch (error) {
            console.error('Error fetching quick suggestions:', error);
            throw error;
        }
    },

    trackQuickSuggestion: async (
        token: string,
        params: {
            featureKey: string;
            clickType?: string;
            device?: string;
        },
    ) => {
        try {
            const response = await chatHttpClient.post(
                '/ai-gateway/api/proxy/chat-service/api/chat/quick-suggestions/track',
                {
                    featureKey: params.featureKey,
                    clickType: params.clickType ?? 'MINI_UI',
                    device: params.device ?? 'web',
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    },
                },
            );
            return response.data;
        } catch (error) {
            console.error('Error tracking quick suggestion click:', error);
            throw error;
        }
    },

    rateMessage: async (token: string, messageId: string, action: 'LIKE' | 'DISLIKE', dislikeReason?: string) => {
        try {
            const response = await chatHttpClient.post(
                apiPath(`/api/chat/messages/action`),
                {
                    action,
                    messageId,
                    dislikeReason
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error rating message:', error);
            throw error;
        }
    },
    // Get pre-signed URLs for S3 upload
    getPresignedUrls: async (token: string, images: Array<{ contentType: string; fileName: string }>) => {
        try {
            const response = await chatHttpClient.post(
                apiPath(`/api/chat/s3/generate-pre-signed-url`),
                {
                    images
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error getting pre-signed URLs:', error);
            throw error;
        }
    },

    addToCart: async (token: string, messageId: number | string, products: Array<{ productId: string; productCode: string }>) => {
        try {
            const response = await chatHttpClient.post(
                apiPath(`/api/chat/messages/add-to-cart`),
                {
                    messageId: messageId.toString(),
                    products: products
                },
                {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('Error adding to cart:', error);
            throw error;
        }
    },

    // Upload file to S3 using pre-signed URL (using native fetch to avoid CORS issues)
    uploadToS3: async (presignedUrl: string, file: File, onProgress?: (percent: number) => void) => {
        try {
            // Use native fetch instead of axios to avoid extra headers that trigger CORS preflight
            const response = await fetch(presignedUrl, {
                method: 'PUT',
                body: file,
                headers: {
                    'Content-Type': file.type,
                    'x-amz-acl': 'private',  // Required: must match signed headers in URL
                },
            });

            if (!response.ok) {
                throw new Error(`S3 upload failed: ${response.status} ${response.statusText}`);
            }

            // Simulate 100% progress on success
            if (onProgress) {
                onProgress(100);
            }

            return response;
        } catch (error) {
            console.error('Error uploading to S3:', error);
            throw error;
        }
    },

    // Get base URL for images based on environment
    getImageBaseUrl: () => {
        const env = REACT_APP_ENV || 'development';

        if (env === 'production' || env === 'prod' || env === 'staging') {
            return 'https://static.tendoo.vn/AI_Services/';
        } else {
            return 'https://myshop-test-123.s3.ap-southeast-1.amazonaws.com/images/';
        }
    },

    // Get user info (full_name, etc.) from user service
    getUserInfo: async (token: string): Promise<AuthInfo | null> => {
        try {
            const baseUrl = USER_API_DOMAIN;
            const response = await axios.get(
                `${baseUrl}/user/v2/auth/info`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            return normalizeAuthInfo(response.data);
        } catch (error) {
            console.error('Error fetching user info:', error);
            return null;
        }
    },

    uploadImages: async (token: string, conversationId: number, files: File[]) => {
        try {
            const formData = new FormData();
            formData.append('conversationId', conversationId.toString());
            files.forEach((file) => {
                formData.append('images', file);
            });

            const response = await axios.post(
                `${CHAT_REST_BASE_URL}${apiPath('/api/chat/s3/upload-images')}`,
                formData,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            return response.data;
        } catch (error) {
            console.error('Error upload images:', error);
            throw error;
        }
    },

    /**
     * Fallback: Get unread bot messages when socket doesn't respond after timeout.
     * Called when thinking exceeds 30 seconds.
     */
    getUnreadMessages: async (
        token: string,
        conversationId: number,
        lastMessageId: string,
        typeChat: string
    ) => {
        try {
            const response = await chatHttpClient.get(
                apiPath(`/api/chat/messages/get-message-unread?conversationId=${conversationId}&lastMessageId=${lastMessageId}&typeChat=${typeChat}&v=2`),
                {
                    headers: {
                        'Authorization': `Bearer ${token}`,
                    }
                }
            );
            return response.data;
        } catch (error) {
            console.error('[Fallback] Error fetching unread messages:', error);
            return null;
        }
    },

    getUserPermission: async (token: string): Promise<UserPermission | null> => {
        try {
            const baseUrl = USER_API_DOMAIN;
            const response = await axios.get(
                `${baseUrl}/business/v2/permissions/current`,
                {
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`,
                    },
                }
            );
            const data = response.data?.data ?? {};
            return {
                isOwner: data.is_owner ?? data.isOwner ?? false,
                permissions: data.permissions ?? {},
            };
        } catch (error) {
            console.error('Error fetching user permission:', error);
            return null;
        }
    },

    listChatComponents: async (
        token: string,
        type: ComponentType,
        options?: { page?: number; pageSize?: number; keyword?: string },
    ): Promise<ChatComponentListPage> => {
        const page = options?.page ?? 1;
        const pageSize = options?.pageSize ?? 10;
        const keyword = (options?.keyword || '').trim();
        const businessId = getBusinessIdFromToken(token);
        const baseUrl = USER_API_DOMAIN;
        const headers = authHeaders(token);

        try {
            let response;
            switch (type) {
                case 'order': {
                    const params = new URLSearchParams();
                    params.set('page', String(page));
                    params.set('page_size', String(pageSize));
                    params.set('sort', 'ordered_at desc');
                    params.set('delivery_method', 'all');
                    params.append('order_statuses', 'waiting_confirm');
                    params.append('order_statuses', 'delivering');
                    params.append('order_statuses', 'complete');
                    params.append('order_statuses', 'return');
                    params.append('order_statuses', 'refund');
                    if (keyword) params.set('search', keyword);
                    response = await axios.get(
                        `${baseUrl}/order/v2/sales-orders?${params.toString()}`,
                        { headers },
                    );
                    break;
                }
                case 'product': {
                    const params = new URLSearchParams();
                    params.set('page', String(page));
                    params.set('page_size', String(pageSize));
                    params.set('sort', 'priority');
                    params.set('has_record_deleted', 'false');
                    if (keyword) params.set('name', keyword);
                    response = await axios.get(
                        `${baseUrl}/product/v2/products?${params.toString()}`,
                        { headers },
                    );
                    break;
                }
                case 'ingredient': {
                    const params = new URLSearchParams();
                    params.set('page', String(page));
                    params.set('page_size', String(pageSize));
                    if (businessId) params.set('business_id', businessId);
                    params.set('name', keyword);
                    params.set('state', '');
                    params.set('sort', '');
                    response = await axios.get(
                        `${baseUrl}/product/v2/materials?${params.toString()}`,
                        { headers },
                    );
                    break;
                }
                case 'customer': {
                    const params = new URLSearchParams();
                    if (businessId) params.set('business_id', businessId);
                    params.set('page', String(page));
                    params.set('page_size', String(pageSize));
                    params.set('sort', 'is_active desc');
                    params.set('state', 'delivering,waiting_confirm');
                    params.set('has_analytic', 'true');
                    params.set('roles', 'customer,both');
                    if (keyword) params.set('search', keyword);
                    response = await axios.get(
                        `${baseUrl}/business/v2/contacts?${params.toString()}`,
                        { headers },
                    );
                    break;
                }
                case 'supplier': {
                    const params = new URLSearchParams();
                    if (businessId) params.set('business_id', businessId);
                    params.set('page', String(page));
                    params.set('page_size', String(pageSize));
                    params.set('sort', 'is_active desc');
                    params.set('state', 'delivering,waiting_confirm');
                    params.set('has_analytic', 'true');
                    params.set('roles', 'supplier,both');
                    if (keyword) params.set('search', keyword);
                    response = await axios.get(
                        `${baseUrl}/business/v2/contacts?${params.toString()}`,
                        { headers },
                    );
                    break;
                }
                case 'employee': {
                    const params = new URLSearchParams();
                    if (businessId) params.set('business_id', businessId);
                    params.set('page', String(page));
                    params.set('page_size', String(pageSize));
                    params.set('direct_permission_key', '');
                    params.set('is_disabled_owner', '');
                    params.set('order_by', '');
                    params.set('sort', '');
                    params.set('search', keyword);
                    params.set('verify_status', '');
                    response = await axios.get(
                        `${baseUrl}/business/v2/staff?${params.toString()}`,
                        { headers },
                    );
                    break;
                }
                default:
                    return { items: [], page, pageSize, hasMore: false };
            }

            const rows = extractListRows(response.data);
            const mapper =
                type === 'order' ? mapOrderItem
                    : type === 'product' ? mapProductItem
                        : type === 'ingredient' ? mapIngredientItem
                            : type === 'customer' ? mapCustomerItem
                                : type === 'supplier' ? mapSupplierItem
                                    : mapEmployeeItem;

            const items = rows.map(mapper).filter((item) => item.id && item.displayName);
            return {
                items,
                page,
                pageSize,
                hasMore: extractHasMore(response.data, page, pageSize, items.length),
            };
        } catch (error) {
            console.error(`Error listing chat components (${type}):`, error);
            throw error;
        }
    },

    getOrCreateAiDisplaySetting: async (token: string): Promise<AiDisplaySetting | null> => {
        try {
            const response = await chatHttpClient.post(
                '/metadata/v2/personalization-settings/get-or-create-full',
                {
                    reference_id: null,
                    reference_type: 'business',
                    sort: 'priority asc',
                    setting_key: 'ai_display',
                    setting_type: 'ai_display',
                    json_value: [
                        { key: 'display', active: true },
                        { key: 'main_screen', active: true },
                        { key: 'all_screen', active: false },
                    ],
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
            return parseAiDisplaySetting(response.data);
        } catch (error) {
            console.error('Error fetching AI display setting:', error);
            throw error;
        }
    },

    updateAiDisplaySetting: async (
        token: string,
        settingId: string,
        isVisible: boolean,
        position: AiDisplayPosition,
        referenceId?: string | null,
    ): Promise<AiDisplaySetting | null> => {
        try {
            const businessId = referenceId || getBusinessIdFromToken(token);
            const response = await chatHttpClient.put(
                `/metadata/v2/personalization-settings/${settingId}`,
                {
                    setting_type: 'ai_display',
                    display_name: '',
                    reference_id: businessId,
                    reference_type: 'business',
                    setting_key: 'ai_display',
                    json_value: buildAiDisplayJsonValue(isVisible, position),
                },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
            return parseAiDisplaySetting(response.data) || {
                id: settingId,
                referenceId: businessId,
                jsonValue: buildAiDisplayJsonValue(isVisible, position),
                isVisible,
                position,
            };
        } catch (error) {
            console.error('Error updating AI display setting:', error);
            throw error;
        }
    },

    /**
     * Create a new message session (new conversation).
     * New session becomes the active one; name starts as null and is set
     * by backend from the first user message (truncated 32 chars).
     * Empty sessions (name=null) stay hidden from GET /sessions list.
     * POST /message-service/api/message/sessions?typeChat=...
     */
    createMessageSession: async (
        token: string,
        typeChat: string = ChatAgentType.AI,
    ): Promise<MessageSession | null> => {
        try {
            const response = await chatHttpClient.post(
                messageServicePath(`/api/message/sessions?typeChat=${encodeURIComponent(typeChat)}`),
                undefined,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            const raw = response.data?.data ?? response.data;
            const session = mapMessageSession(raw) ?? (() => {
                const id = parseMessageSessionId(response.data);
                return id ? mapMessageSession({ ...raw, id }) : null;
            })();

            if (!session) {
                console.error('[Session] POST create returned no id', response.data);
                return null;
            }

            persistMessageSessionId(session.id);
            return session;
        } catch (error) {
            console.error('Error creating message session:', error);
            throw error;
        }
    },

    /**
     * Get current active message session (open app / reload).
     * Backend returns active session or auto-creates a new one after 06:00 cycle.
     * GET /message-service/api/message/sessions/current?typeChat=...
     */
    getCurrentMessageSession: async (
        token: string,
        typeChat: string = ChatAgentType.AI,
        options?: { signal?: AbortSignal },
    ): Promise<CurrentMessageSession | null> => {
        try {
            const response = await chatHttpClient.get(
                `${messageServicePath('/api/message/sessions/current')}?typeChat=${encodeURIComponent(typeChat)}&device=web`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                    signal: options?.signal,
                },
            );

            const raw = response.data?.data ?? response.data;
            const sessionId =
                (raw?.sessionId != null && String(raw.sessionId).trim()
                    ? String(raw.sessionId)
                    : null) || parseMessageSessionId(response.data);

            const newSession = Boolean(raw?.newSession ?? raw?.new_session);

            // newSession=true có thể chưa có sessionId — FE sẽ POST create
            if (!sessionId && !newSession) return null;

            return {
                sessionId: sessionId || '',
                newSession,
            };
        } catch (error) {
            if (axios.isCancel(error) || (error as any)?.code === 'ERR_CANCELED') {
                return null;
            }
            console.error('Error fetching current message session:', error);
            throw error;
        }
    },

    /**
     * List message sessions (cursor pagination, lastMessageAt DESC).
     * GET /message-service/api/message/sessions
     * Sessions with name=null (no messages) are hidden by backend.
     */
    getMessageSessions: async (
        token: string,
        params: GetMessageSessionsParams = {},
    ): Promise<MessageSessionsPage> => {
        try {
            const {
                size = 20,
                typeChat,
                keyword,
                nextCursor,
                previousCursor,
                direction = 'NEXT',
            } = params;

            const query = new URLSearchParams();
            query.set('size', String(Math.min(Math.max(size, 1), 50)));
            query.set('direction', direction);
            if (typeChat) query.set('typeChat', typeChat);
            if (keyword?.trim()) query.set('keyword', keyword.trim());
            if (direction === 'NEXT' && nextCursor) {
                query.set('nextCursor', nextCursor);
            }
            if (direction === 'PREV' && previousCursor) {
                query.set('previousCursor', previousCursor);
            }

            const response = await chatHttpClient.get(
                `${messageServicePath('/api/message/sessions')}?${query.toString()}`,
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );

            return parseMessageSessionsPage(response.data);
        } catch (error) {
            console.error('Error fetching message sessions:', error);
            throw error;
        }
    },

    /**
     * Rename a message session.
     * PUT /message-service/api/message/sessions/name/{id}
     * name: required, non-blank, max 32 chars
     */
    renameMessageSession: async (
        token: string,
        sessionId: string,
        name: string,
    ): Promise<string> => {
        const nextName = name.trim();
        if (!nextName) {
            throw new Error('Session name must not be blank');
        }
        if (nextName.length > 32) {
            throw new Error('Session name must not exceed 32 characters');
        }

        try {
            await chatHttpClient.put(
                messageServicePath(`/api/message/sessions/name/${encodeURIComponent(sessionId)}`),
                { name: nextName },
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                        'Content-Type': 'application/json',
                    },
                },
            );
            return nextName;
        } catch (error) {
            console.error('Error renaming message session:', error);
            throw error;
        }
    },

    /**
     * Soft-delete a message session (hidden from list, kept in DB).
     * DELETE /message-service/api/message/sessions/{id}
     */
    deleteMessageSession: async (token: string, sessionId: string): Promise<void> => {
        try {
            await chatHttpClient.delete(
                messageServicePath(`/api/message/sessions/${encodeURIComponent(sessionId)}`),
                {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                },
            );
        } catch (error) {
            console.error('Error deleting message session:', error);
            throw error;
        }
    },
};